import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bike, Search, Store, X } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CategoryNavigation } from "@/components/menu/CategoryNavigation";
import { ProductCard } from "@/components/menu/ProductCard";
import { EmptyState, ErrorState, ProductSkeletonList, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { branchesQuery, categoriesQuery, menuItemsQuery } from "@/lib/queries";
import { loc } from "@/lib/format";
import { activeMenuCategory } from "@/lib/menu-model";
import { useBranchOrdering } from "@/lib/use-branch-ordering";
import { BranchOrderNotice } from "@/components/BranchOrderNotice";

export const Route = createFileRoute("/menu/")({
  component: OnlineMenu,
});

const PAGE_SIZE = 8;

function OnlineMenu() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { cart, hydrated } = useCart();
  const orderType = cart.orderType;
  const serviceType = orderType?.type ?? "pickup";

  const [categoryId, setCategoryId] = useState("");
  const [term, setTerm] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);

  const branches = useQuery(branchesQuery());
  const branchId = orderType?.branchId ?? "";
  const categories = useQuery(categoriesQuery(branchId, serviceType));
  const activeCategory = activeMenuCategory(categories.data ?? [], categoryId);
  const searchTerm = term.trim();

  const items = useQuery({
    ...menuItemsQuery({
      ...(branchId ? { branchId } : {}),
      ...(!searchTerm && activeCategory ? { categoryId: activeCategory } : {}),
      orderType: serviceType,
      ...(searchTerm ? { name: searchTerm } : {}),
      limit,
    }),
    enabled: Boolean(branchId) && (Boolean(searchTerm) || Boolean(activeCategory)),
  });

  const list = items.data?.data ?? [];
  const total = items.data?.total ?? 0;
  const branch = branches.data?.find((b) => b.id === branchId);
  const { block } = useBranchOrdering();

  useEffect(() => {
    if (hydrated && !orderType) navigate({ to: "/order-type", replace: true });
  }, [hydrated, navigate, orderType]);

  function requireOrderType() {
    if (orderType) return true;
    toast.error(t("menu.orderTypeRequired"));
    navigate({ to: "/order-type" });
    return false;
  }

  if (!hydrated || !orderType) {
    return <PageShell><p className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"><Spinner /> {t("common.loading")}</p></PageShell>;
  }

  return (
    <>
      <PageShell>
        <div className="mb-5"><BranchOrderNotice reason={block} /></div>
        <div className="mb-6">
          <div>
            <h1 className="text-3xl font-black sm:text-4xl">{t("menu.title")}</h1>
            <p className="mt-2 text-muted-foreground">{t("menu.subtitle")}</p>
          </div>

          <div className="mt-5 flex w-full justify-center">
            <div className="flex w-full max-w-xl items-center gap-3 rounded-2xl border-2 border-primary/20 bg-card p-3 shadow-soft sm:w-[32rem] sm:p-4">
              {orderType ? (
                <>
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                    {orderType.type === "delivery" ? <Bike className="size-5" /> : <Store className="size-5" />}
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="text-xs font-bold text-muted-foreground">{t("orderType.method")}</p>
                    <p className="mt-0.5 text-base font-black">
                      {orderType.type === "delivery" ? t("menu.delivery") : t("menu.pickup")}
                    </p>
                    {branch ? <p className="truncate text-muted-foreground">{loc(branch.name, lang)}</p> : null}
                  </div>
                  <Button asChild size="sm" className="shrink-0">
                    <Link to="/order-type">{t("common.change")}</Link>
                  </Button>
                </>
              ) : (
                <Button asChild size="sm">
                  <Link to="/order-type">{t("menu.selectOrderType")}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <CategoryNavigation
            categories={categories.data ?? []}
            activeCategoryId={activeCategory}
            searching={Boolean(searchTerm)}
            onSelect={(id) => {
              setTerm("");
              setLimit(PAGE_SIZE);
              setCategoryId(id);
            }}
          />

          <div className="grid min-w-0 content-start gap-4">
            <div className="relative">
              <Search className="pointer-events-none absolute start-4 top-4 size-5 text-muted-foreground" aria-hidden="true" />
              <Input
                value={term}
                type="search"
                onChange={(e) => { setLimit(PAGE_SIZE); setTerm(e.target.value); }}
                placeholder={t("menu.searchProducts")}
                aria-label={t("menu.searchProducts")}
                className="h-13 rounded-2xl ps-12 pe-12 [&::-webkit-search-cancel-button]:hidden"
              />
              {term ? <Button variant="ghost" size="icon" className="absolute end-1 top-1" aria-label={t("menu.clearSearch")} onClick={() => setTerm("")}><X /></Button> : null}
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-bold">{searchTerm ? t("menu.results", { term: searchTerm }) : loc(categories.data?.find((category) => category.id === activeCategory)?.name, lang)}</h2>
              {items.data ? <p className="text-xs text-muted-foreground" role="status">{t("menu.resultsCount", { shown: list.length, total })}</p> : null}
            </div>

            {categories.isError ? (
              <ErrorState error={categories.error} onRetry={() => categories.refetch()} />
            ) : categories.isPending ? (
              <ProductSkeletonList />
            ) : !activeCategory && !searchTerm ? (
              <EmptyState title={t("menu.noCategories")} description={t("menu.noCategoriesHint")} action={<Button asChild variant="outline"><Link to="/order-type">{t("checkout.changeType")}</Link></Button>} />
            ) : items.isPending ? (
              <ProductSkeletonList />
            ) : items.isError ? (
              <ErrorState error={items.error} onRetry={() => items.refetch()} />
            ) : list.length === 0 ? (
              <EmptyState title={searchTerm ? t("menu.noResults") : t("menu.emptyCategory")} description={searchTerm ? t("menu.noResultsHint") : undefined} action={searchTerm ? <Button variant="outline" onClick={() => setTerm("")}>{t("menu.clearSearch")}</Button> : undefined} />
            ) : (
              <>
                {list.map((item) => (
                  <ProductCard key={item.id} item={item} onRequireOrderType={requireOrderType} />
                ))}
                {list.length < total ? (
                  <Button variant="outline" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                    {t("common.loadMore")}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </PageShell>
    </>
  );
}
